import { createLoader } from '@meteor/data-hooks';
import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { withAuth } from '../../user';
import { organizationClient } from '../client';

export let organizationsLoader = createLoader({
  name: 'organizations',
  fetch: () => withAuth(() => organizationClient.organization.list({})),
  mutators: {
    create: (i: { name: string }) => withAuth(() => organizationClient.organization.create(i))
  }
});

export let useOrganizations = () => {
  let organizations = organizationsLoader.use();

  return {
    ...organizations,
    createMutator: organizations.useMutator('create')
  };
};

export let organizationLoader = createLoader({
  name: 'organization',
  parents: [organizationsLoader],
  fetch: (i: { organizationId: string }) =>
    withAuth(() => organizationClient.organization.get(i)),
  mutators: {
    update: (
      i: {
        name?: string;
        imageFileId?: string | null;
      },
      { output: { id } }
    ) =>
      withAuth(() =>
        organizationClient.organization.update({
          ...i,
          organizationId: id
        })
      ),

    delete: (_, { output: { id } }) =>
      withAuth(() =>
        organizationClient.organization.delete({
          organizationId: id
        })
      ),

    createProject: (
      i: {
        name: string;
      },
      { output: { id } }
    ) =>
      withAuth(() =>
        organizationClient.project.create({
          organizationId: id,
          ...i
        })
      )
  }
});

export let useOrganization = (organizationId: string | null) => {
  let organization = organizationLoader.use(organizationId ? { organizationId } : null);

  return {
    ...organization,
    updateMutator: organization.useMutator('update'),
    deleteMutator: organization.useMutator('delete')
  };
};

export let useCurrentOrganization = () => {
  let orgs = useOrganizations();

  let params = useParams<{ organizationId: string; instanceId: string }>();
  let [search] = useSearchParams();

  let organizationId =
    params.organizationId || search.get('organizationId') || search.get('organization_id');
  let instanceId = params.instanceId || search.get('instanceId') || search.get('instance_id');

  let foundOrg = useMemo(() => {
    if (!organizationId && !instanceId) return null;

    return orgs.data?.find(
      org =>
        org.id == organizationId ||
        org.slug == organizationId ||
        org.projects.some(p =>
          p.instances.some(i => i.id == instanceId || i.slug == instanceId)
        )
    );
  }, [orgs.data, organizationId, instanceId]);

  return useOrganization(foundOrg?.id || null);
};

export let useProjects = (organizationId: string | null) => {
  let org = organizationLoader.use(organizationId ? { organizationId } : null);

  return {
    ...org,
    data: org.data?.projects,
    createMutator: org.useMutator('createProject')
  };
};

export let useCurrentProjects = () => {
  let org = useCurrentOrganization();

  return useProjects(org.data?.id || null);
};
